import { useEffect, useState } from 'react';
import { listProviders } from '../../lib/api-client';
import { useDashboardSessionKey } from '../../app/DashboardSessionKeyContext';
import { useApiKeys, type OnboardingApiKey } from '../onboarding/ApiKeysContext';
import { useAuth } from '../onboarding/AuthContext';

export interface UseQuoteApiKeyResult {
  /** `null` until the right key is ready — callers should disable the form/show a loading state until then. */
  activeKey: OnboardingApiKey | null;
  /** `true` when the org has no `active` provider connection yet — mirrors `DemoStep.tsx`'s onboarding-time check, now read from `GET /v1/providers` instead of in-memory onboarding state. */
  isSandbox: boolean;
  loading: boolean;
  error: string | null;
}

/**
 * Decides which API key authenticates `POST /v1/quotes` outside onboarding
 * (TTP-287): `trv_live_` when the org has at least one provider with
 * `status: 'active'`, `trv_test_` (sandbox, zero real provider call —
 * CLAUDE.md domain rule #4) otherwise. Same rule `DemoStep.tsx` applies
 * during onboarding, just re-derived from the org's current provider
 * connections (`GET /v1/providers`) instead of the onboarding-only
 * `connectedProviders` list, which doesn't exist anymore once onboarding is
 * done.
 *
 * Reuses the app-wide `ApiKeysContext` (mounted in `AppProviders`, not just
 * inside onboarding routes) for the sandbox key — same idempotent
 * find-or-mint mechanics as onboarding's Tela 3/4, never a second sandbox
 * key. The live key is the dashboard's own session key
 * (`DashboardSessionKeyContext.tsx`), already `mode: 'live'` and already
 * ensured by `AppShell`.
 */
export function useQuoteApiKey(): UseQuoteApiKeyResult {
  const { user } = useAuth();
  const { key: sessionKey } = useDashboardSessionKey();
  const { keys, ensureKeys, loading: keysLoading, error: keysError } = useApiKeys();
  const [isSandbox, setIsSandbox] = useState<boolean | null>(null);
  const [checkError, setCheckError] = useState<string | null>(null);

  useEffect(() => {
    if (!sessionKey) {
      return;
    }
    let cancelled = false;
    setIsSandbox(null);
    setCheckError(null);
    listProviders(sessionKey.key)
      .then((connections) => {
        if (!cancelled) {
          setIsSandbox(!connections.some((connection) => connection.status === 'active'));
        }
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setCheckError(err instanceof Error ? err.message : 'Não foi possível verificar seus provedores conectados.');
        }
      });
    return () => {
      cancelled = true;
    };
  }, [sessionKey]);

  useEffect(() => {
    if (isSandbox !== true || keys.test || keysLoading || !user) {
      return;
    }
    user.getIdToken().then((idToken) => ensureKeys(idToken)).catch(() => {
      // ensureKeys already stores its own error in ApiKeysContext; nothing extra to do here.
    });
  }, [isSandbox, keys.test, keysLoading, user, ensureKeys]);

  const loading = isSandbox === null || (isSandbox === true && !keys.test);
  const activeKey = isSandbox === null ? null : isSandbox ? keys.test : sessionKey;
  const error = checkError ?? (isSandbox === true ? keysError : null);

  return { activeKey, isSandbox: isSandbox ?? false, loading, error };
}
